/**
 * Notification context — in-app bell icon notifications.
 *
 * Hydrates the unread list/count from the REST API on mount, then listens
 * for live "notification" Socket.IO pushes for instant updates. The socket
 * connection here is the *global* one — AppLayout mounts this provider for
 * every authenticated page, so the bell stays live regardless of which page
 * the user is on (previously the socket only connected on /assistant).
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { notification as antdNotification } from "antd";
import axiosInstance from "../utils/axiosInstance";
import { getTenantId } from "../utils/auth";
import socketService from "../services/socketService";

export interface NotificationItem {
  id: string;
  notification_type: string;
  title: string;
  message: string | null;
  entity_type: string;
  entity_id: string;
  job_type: string | null;
  action_url: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

interface NotificationContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  refetch: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  markAsRead: () => {},
  markAllAsRead: () => {},
  refetch: () => {},
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/api/v1/notifications", {
        params: { unread_only: true, limit: 50 },
      });
      const list: NotificationItem[] = res.data?.notifications || [];
      setNotifications(list);
      setUnreadCount(list.length);
    } catch {
      /* non-fatal — bell just stays at whatever it last knew */
    }
  }, []);

  useEffect(() => {
    fetchUnread();
  }, [fetchUnread]);

  // Extra catch-up: refetch whenever the tab regains focus/visibility —
  // covers any missed live push beyond just socket reconnects (e.g. the
  // backend found no registered socket for this user at push time at all).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchUnread();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [fetchUnread]);

  // Polling fallback: catch any notification the socket missed (disconnected
  // at push time, wrong port in dev, etc.) without relying on tab-focus.
  useEffect(() => {
    const id = setInterval(fetchUnread, 60_000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  // Global socket connection — socketService.connect() already no-ops if a
  // socket is already live, so it's safe to call on every effect run without
  // an extra guard here. (A `useRef` guard around this used to also block
  // re-registering the "notification" listener on StrictMode's dev-mode
  // double mount/cleanup/mount, leaving a connected socket with zero
  // listeners attached — reload-triggered fetchUnread() masked it, but live
  // pushes never arrived. Let onEvent/offEvent pair naturally per effect run
  // instead.)
  useEffect(() => {
    let userLabel = "user";
    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        const decoded: any = jwtDecode(token);
        userLabel = decoded.email || decoded.sub || "user";
      }
    } catch {
      /* cosmetic only — backend derives real identity from the JWT itself */
    }

    socketService.connect(userLabel, getTenantId());

    // Catch-up sync: a live "notification" push can be missed if the socket
    // happens to be disconnected/reconnecting at the exact moment the
    // backend pushes (network blip, laptop sleep, tab backgrounded, etc.) —
    // socket.io's "connect" event fires both for the initial connection and
    // every successful reconnection, so re-fetching here means a missed
    // push self-heals the next time the socket comes back up, instead of
    // requiring a manual page reload.
    const handleReconnect = () => { fetchUnread(); };
    socketService.onEvent("connect", handleReconnect);

    const handleNotification = (payload: any) => {
      setNotifications(prev => [
        {
          id: payload.id,
          notification_type: payload.notification_type,
          title: payload.title,
          message: payload.message,
          entity_type: payload.entity_type,
          entity_id: payload.entity_id,
          job_type: payload.job_type,
          action_url: payload.action_url,
          is_read: false,
          read_at: null,
          created_at: payload.created_at,
        },
        ...prev,
      ]);
      setUnreadCount(prev =>
        typeof payload.unread_count === "number" ? payload.unread_count : prev + 1
      );

      // Surface completions/failures as a toast too, not just the bell —
      // the bell is easy to miss if the user isn't looking at the header.
      if (payload.notification_type === "job_completed") {
        antdNotification.success({
          message: payload.title,
          description: payload.message,
          placement: "topRight",
          onClick: () => {
            if (payload.action_url) navigate(payload.action_url);
          },
        });
      } else if (payload.notification_type === "job_failed") {
        antdNotification.error({
          message: payload.title,
          description: payload.message,
          placement: "topRight",
          onClick: () => {
            if (payload.action_url) navigate(payload.action_url);
          },
        });
      }
    };

    socketService.onEvent("notification", handleNotification);
    return () => {
      socketService.offEvent("connect", handleReconnect);
      socketService.offEvent("notification", handleNotification);
    };
  }, [fetchUnread]);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    setUnreadCount(prev => Math.max(0, prev - 1));
    axiosInstance.post(`/api/v1/notifications/${id}/read`).catch(() => {
      // Revert on failure so the bell doesn't silently lose a notification
      fetchUnread();
    });
  }, [fetchUnread]);

  const markAllAsRead = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    axiosInstance.post("/api/v1/notifications/read-all").catch(() => {
      fetchUnread();
    });
  }, [fetchUnread]);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, markAsRead, markAllAsRead, refetch: fetchUnread }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
